import { MapTileType } from '../../editor/MapTypes';

/**
 * Pattern generation functions for default maps.
 * Each function takes a 20x20 grid and fills it with walls to create a maze pattern.
 * Spawn and goal positions are placed randomly after pattern generation.
 * 
 * Note: These functions modify the grid in-place. The grid should be initialized
 * with all 'empty' tiles before calling these functions.
 */

export function generateSpiralPattern(grid: MapTileType[][]): void {
  // Spiral pattern: concentric walls creating a spiral path
  const size = 20;
  
  // Create spiral walls with openings
  for (let layer = 0; layer < 4; layer++) {
    const start = layer * 2;
    const end = size - 1 - layer * 2;
    
    // Top wall with opening
    for (let x = start; x <= end; x++) {
      if (x !== start + 1 || layer === 0) { // Opening on left except first layer
        grid[start][x] = 'wall';
      }
    }
    
    // Right wall with opening
    for (let y = start; y <= end; y++) {
      if (y !== end - 1 || layer === 0) { // Opening on bottom except first layer
        grid[y][end] = 'wall';
      }
    }
    
    // Bottom wall with opening
    for (let x = end; x >= start; x--) {
      if (x !== end - 1 || layer === 0) { // Opening on right except first layer
        grid[end][x] = 'wall';
      }
    }
    
    // Left wall with opening
    for (let y = end; y >= start; y--) {
      if (y !== start + 1 || layer === 0) { // Opening on top except first layer
        grid[y][start] = 'wall';
      }
    }
  }
  
  // Pattern complete - spawn/goal will be placed randomly by DefaultMapGenerator
}

export function generateZigZagPattern(grid: MapTileType[][]): void {
  // Zig-zag: alternating horizontal corridors
  const size = 20;
  
  // Create horizontal walls with alternating gaps
  for (let y = 0; y < size; y++) {
    if (y % 4 === 0 && y > 0 && y < 19) {
      // Full horizontal wall with gap on alternating sides
      const gapSide = (y / 4) % 2 === 0 ? 'left' : 'right';
      for (let x = 0; x < size; x++) {
        if (gapSide === 'left' && x < 5) {
          grid[y][x] = 'empty'; // Gap on left
        } else if (gapSide === 'right' && x >= 15) {
          grid[y][x] = 'empty'; // Gap on right
        } else {
          grid[y][x] = 'wall';
        }
      }
    }
  }
  
  // Pattern complete - spawn/goal will be placed randomly by DefaultMapGenerator
}

export function generateGridLabyrinthPattern(grid: MapTileType[][]): void {
  // Grid-based maze with multiple paths
  const size = 20;
  
  // Create grid pattern with deterministic openings
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (x % 4 === 0 || y % 4 === 0) {
        // Create grid with strategic openings (deterministic based on position)
        const shouldBeWall = (x + y * 3) % 5 !== 0; // Deterministic pattern
        if (shouldBeWall) {
          grid[y][x] = 'wall';
        }
      }
    }
  }
  
  // Pattern complete - spawn/goal will be placed randomly by DefaultMapGenerator
}

export function generateSnakePathPattern(grid: MapTileType[][]): void {
  // Single winding S-curve path
  const size = 20;
  const path: Array<{x: number, y: number}> = [];
  
  // Create snake path: start at spawn, wind to goal
  let x = 2, y = 2;
  let direction = 'right';
  const maxSteps = 400; // Safety limit to prevent infinite loops
  let steps = 0;
  
  // Create snake path from top-left to bottom-right area
  const targetX = 16;
  const targetY = 16;
  while ((x < targetX || y < targetY) && steps < maxSteps) {
    path.push({x, y});
    steps++;
    
    // Move based on direction
    if (direction === 'right') {
      if (x < 16) {
        x++;
        // Switch to down at certain x positions to create S-curve
        if (x === 6 || x === 12) {
          direction = 'down';
        }
      } else {
        direction = 'down';
        y++;
      }
    } else if (direction === 'down') {
      if (y < 16) {
        y++;
        // Switch direction when reaching certain y positions
        if (y === 6) {
          direction = 'left';
        } else if (y === 10) {
          direction = 'right';
        } else if (y === 14) {
          direction = 'left';
        }
      } else {
        // Near goal, go right
        direction = 'right';
        x++;
      }
    } else if (direction === 'left') {
      if (x > 3) {
        x--;
        // Switch to down at certain x positions
        if (x === 9 || x === 3) {
          direction = 'down';
        }
      } else {
        direction = 'down';
        y++;
      }
    }
    
    // Safety check: if we're close to target, go directly
    if (x >= targetX && y >= targetY) {
      break;
    }
  }
  
  // Ensure we reach the target
  if (x !== targetX || y !== targetY) {
    // Create direct path to target
    while (x < targetX) {
      x++;
      path.push({x, y});
    }
    while (y < targetY) {
      y++;
      path.push({x, y});
    }
  } else {
    path.push({x: targetX, y: targetY});
  }
  
  // Fill everything except path
  for (let py = 0; py < size; py++) {
    for (let px = 0; px < size; px++) {
      const onPath = path.some(p => p.x === px && p.y === py);
      if (!onPath) {
        grid[py][px] = 'wall';
      }
    }
  }
}

export function generateChamberPattern(grid: MapTileType[][]): void {
  // Multiple connected chambers
  const size = 20;
  
  // Create chambers
  const chambers = [
    {x: 0, y: 0, w: 8, h: 8},
    {x: 12, y: 0, w: 8, h: 8},
    {x: 0, y: 12, w: 8, h: 8},
    {x: 12, y: 12, w: 8, h: 8},
  ];
  
  // Fill outside chambers with walls
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const inChamber = chambers.some(c => 
        x >= c.x && x < c.x + c.w && y >= c.y && y < c.y + c.h
      );
      if (!inChamber) {
        grid[y][x] = 'wall';
      }
    }
  }
  
  // Create corridors between chambers
  grid[4][8] = 'empty';
  grid[4][11] = 'empty';
  grid[12][8] = 'empty';
  grid[12][11] = 'empty';
  grid[8][4] = 'empty';
  grid[11][4] = 'empty';
  grid[8][12] = 'empty';
  grid[11][12] = 'empty';
  
  // Pattern complete - spawn/goal will be placed randomly by DefaultMapGenerator
}

export function generateHoneycombPattern(grid: MapTileType[][]): void {
  // Hexagonal-like pattern
  const size = 20;
  
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Create hexagonal pattern with openings
      const offset = y % 2 === 0 ? 0 : 1;
      const hexX = (x + offset) % 6;
      const hexY = y % 4;
      
      // Create walls in hexagonal pattern but leave openings
      if ((hexX === 0 || hexX === 3) && (hexY === 0 || hexY === 2)) {
        grid[y][x] = 'wall';
      }
    }
  }
  
  // Pattern complete - spawn/goal will be placed randomly by DefaultMapGenerator
}

export function generateRiverDeltaPattern(grid: MapTileType[][]): void {
  // Multiple branching paths
  const size = 20;
  
  // Create branching paths pattern
  // Main path (vertical then horizontal)
  for (let y = 2; y < 10; y++) {
    grid[y][2] = 'empty';
  }
  for (let x = 2; x < 10; x++) {
    grid[10][x] = 'empty';
  }
  
  // Branch 1 (vertical then horizontal)
  for (let y = 10; y < 17; y++) {
    grid[y][10] = 'empty';
  }
  for (let x = 10; x < 17; x++) {
    grid[17][x] = 'empty';
  }
  
  // Branch 2 (vertical then horizontal)
  for (let y = 10; y < 15; y++) {
    grid[y][15] = 'empty';
  }
  for (let x = 15; x < 17; x++) {
    grid[15][x] = 'empty';
  }
  
  // Fill rest with walls
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (grid[y][x] !== 'empty') {
        grid[y][x] = 'wall';
      }
    }
  }
}

export function generateBoxMazePattern(grid: MapTileType[][]): void {
  // Nested boxes
  const size = 20;
  
  // Create nested boxes
  const boxes = [
    {x: 1, y: 1, w: 18, h: 18},
    {x: 4, y: 4, w: 12, h: 12},
    {x: 7, y: 7, w: 6, h: 6},
  ];
  
  boxes.forEach((box, idx) => {
    // Top
    for (let x = box.x; x < box.x + box.w; x++) {
      grid[box.y][x] = 'wall';
    }
    // Bottom
    for (let x = box.x; x < box.x + box.w; x++) {
      grid[box.y + box.h - 1][x] = 'wall';
    }
    // Left
    for (let y = box.y; y < box.y + box.h; y++) {
      grid[y][box.x] = 'wall';
    }
    // Right
    for (let y = box.y; y < box.y + box.h; y++) {
      grid[y][box.x + box.w - 1] = 'wall';
    }
    
    // Create openings
    if (idx < boxes.length - 1) {
      grid[box.y + 2][box.x + box.w - 1] = 'empty';
      grid[box.y + 2][box.x] = 'empty';
    }
  });
  
  // Pattern complete - spawn/goal will be placed randomly by DefaultMapGenerator
}

export function generateDiagonalFlowPattern(grid: MapTileType[][]): void {
  // Diagonal corridors
  const size = 20;
  
  // Create diagonal walls with deterministic pattern
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const diag1 = (x + y) % 5 === 0;
      const diag2 = (x - y + size) % 5 === 0;
      
      if (diag1 || diag2) {
        // Deterministic pattern based on position
        const shouldBeWall = (x * 3 + y * 7) % 7 < 4;
        if (shouldBeWall) {
          grid[y][x] = 'wall';
        }
      }
    }
  }
  
  // Pattern complete - spawn/goal will be placed randomly by DefaultMapGenerator
}

export function generateOpenArenaPattern(grid: MapTileType[][]): void {
  // Minimal walls, mostly open
  const size = 20;
  
  // Create strategic chokepoints
  // Vertical chokepoint
  for (let y = 5; y < 15; y++) {
    grid[y][10] = 'wall';
  }
  
  // Horizontal chokepoint
  for (let x = 5; x < 15; x++) {
    grid[10][x] = 'wall';
  }
  
  // Few scattered walls
  grid[5][5] = 'wall';
  grid[5][15] = 'wall';
  grid[15][5] = 'wall';
  grid[15][15] = 'wall';
  
  // Rest is empty (already initialized)
}

// Note: Path validation and spawn/goal placement is handled by DefaultMapGenerator
// Pattern functions only generate the maze structure

